import { Button, Form, Input, Tooltip } from "antd";
import Upload from "antd/es/upload/Upload";
import { useContext, useEffect, useRef, useState } from "react";
import {
  Check,
  Clipboard,
  Edit2,
  RefreshCcw,
  Trash2,
  Upload as UploadIcon,
} from "react-feather";
import { useTranslation } from "react-i18next";
import MessageModal from "../../components/MessageModal";
import {
  fetchCheckDomain,
  useGetMeApi,
  useUpdateSetting,
} from "../../services/apis/user.api";
import { useRootSelector } from "../../stores/reducers/root";
import { AbilityContext, PERMISSION } from "../../services/ability.service";
import { copyToClipboard } from "../../utils/clipboard";
import { CrownOutlined } from "@ant-design/icons";

export default function Setting() {
  const { t } = useTranslation();
  const { refetch } = useGetMeApi();
  const ability = useContext(AbilityContext);
  const [copy, setCopy] = useState<number[]>([]);

  const [uploadedFile, setUpLoadedFile] = useState<File | string>();
  const [uploadedFileDark, setUpLoadedFileDark] = useState<File | string>();
  const [fav, setFav] = useState<File | string>();
  const [metaImage, setMetaImage] = useState<File | string>();

  const [validUploaded, setValidUploaded] = useState<string>("");
  const [validUploadedDark, setValidUploadedDark] = useState<string>("");
  const [validMetaImage, setValidMetaImage] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const inputRefDark = useRef<HTMLInputElement>(null);
  const inputRefMeta = useRef<HTMLInputElement>(null);

  const [form] = Form.useForm();
  const user = useRootSelector((state) => state.user.userProfile);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingReload, setIsLoadingReload] = useState<boolean>(false);
  const [notification, setNotification] = useState<string | null>(null);

  useEffect(() => {
    form.setFieldsValue({
      sub_domain_name: user?.setting?.sub_domain_name
        ? user?.setting?.sub_domain_name?.split(".")?.shift()
        : "",
      domain: user?.setting?.domain || "",
      watermark: user?.setting?.watermark || "",
    });
    setUpLoadedFile(user?.setting?.main_logo || "");
    setUpLoadedFileDark(user?.setting?.main_logo_dark || "");
    setFav(user?.setting?.fav_logo || "");
    setMetaImage(user?.setting?.meta_image || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const mutation = useUpdateSetting();

  const sub_domain_name = Form.useWatch("sub_domain_name", form);
  const domain = Form.useWatch("domain", form);
  const onFinish = (values: any) => {
    const formData = new FormData();
    if (typeof uploadedFile === "object") {
      formData.append("main_logo", uploadedFile);
    }

    if (typeof uploadedFileDark === "object") {
      formData.append("main_logo_dark", uploadedFileDark);
    }

    if (typeof fav === "object") {
      formData.append("fav_logo", fav);
    }

    if (typeof metaImage === "object") {
      formData.append("meta_image", metaImage);
    }

    formData.append("sub_domain_name", values?.sub_domain_name);
    formData.append("domain", values?.domain);
    formData.append("watermark", values?.watermark || "");
    formData.append("_method", "PUT");

    // if (!!inputRef.current) inputRef.current.value = '';

    mutation.mutate(formData, {
      onSuccess: (res) => {
        setError("");
        setNotification(t("formProfile.success"));
        refetch();
      },
      onError: (error) => {
        setError(error.response?.data.message || t("formProfile.error"));
        setNotification("");
      },
    });
  };

  const handleOpenFile = () => {
    if (!!inputRef.current) inputRef.current.click();
  };

  const handleOpenFileDark = () => {
    if (!!inputRefDark.current) inputRefDark.current.click();
  };

  const handleOpenFileMeta = () => {
    if (!!inputRefMeta.current) inputRefMeta.current.click();
  };

  const handleChange = (event: any) => {
    if (event?.target.files) {
      setUpLoadedFile(event?.target.files[0]);
    }
  };

  const handleChangeDark = (event: any) => {
    if (event?.target.files) {
      setUpLoadedFileDark(event?.target.files[0]);
    }
  };

  const handleChangeMeta = (event: any) => {
    if (event?.target.files) {
      setMetaImage(event?.target.files[0]);
    }
  };

  const handleClick = () => {
    setError("");
    setNotification("");
    if (typeof uploadedFile === "string" || !uploadedFile) {
      return;
    } else {
      if (uploadedFile.size / 1024 / 1024 > 10) {
        const temp = t("formCreateAlbum.checkValidImage.size10");
        setValidUploaded(temp);
        return;
      }
    }
  };

  const handleClickDark = () => {
    setError("");
    setNotification("");
    if (typeof uploadedFileDark === "string" || !uploadedFileDark) {
      return;
    } else {
      if (uploadedFileDark.size / 1024 / 1024 > 10) {
        const temp = t("formCreateAlbum.checkValidImage.size10");
        setValidUploadedDark(temp);
        return;
      }
    }
  };

  const handleClickMeta = () => {
    setError("");
    setNotification("");
    if (typeof metaImage === "string" || !metaImage) {
      return;
    } else {
      if (metaImage.size / 1024 / 1024 > 10) {
        const temp = t("formCreateAlbum.checkValidImage.size10");
        setValidMetaImage(temp);
        return;
      }
    }
  };

  return (
    <>
      <div className="relative py-8 flex flex-col items-center md:max-w-[686px] m-auto">
        <div className="text-slate-700 font-semibold text-xl sm:text-2xl">
          {t("formSetting.title")}
        </div>
        {/* <div className="relative w-full h-[1px] bg-slate-900/30 text-slate-500 my-5 text-sm">
          <span className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 bg-white px-3 whitespace-nowrap">
            {t("formSetting.subTitle")}
          </span>
        </div> */}
        <Form
          form={form}
          name="register-form"
          layout="vertical"
          className="w-full flex flex-col mt-4"
          onFinish={onFinish}
        >
          <Form.Item
            name="sub_domain_name"
            label={
              <span className="flex items-center gap-1">
                {t("formProfile.subdomainName")}
                {!ability.can("can", PERMISSION.PRIVATE_DOMAIN) && (
                  <Tooltip title={t("formSetting.subTitle")}>
                    <CrownOutlined className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  </Tooltip>
                )}
              </span>
            }
            validateFirst
            rules={[
              {
                pattern: /^[a-zA-Z0-9]+$/,
                message: t("formSetting.subdomainNameError"),
              },
            ]}
          >
            <div className="flex items-end">
              <Input
                autoFocus
                value={sub_domain_name}
                className="py-2 px-3"
                disabled={!ability.can("can", PERMISSION.PRIVATE_DOMAIN)}
              />
              <span className="ml-2">.shotpik.com</span>
            </div>
          </Form.Item>

          <Form.Item
            name="domain"
            label={
              <span className="flex items-center gap-1">
                {t("formProfile.domain")}
                {!ability.can("can", PERMISSION.PRIVATE_DOMAIN) && (
                  <Tooltip title={t("formSetting.subTitle")}>
                    <CrownOutlined className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  </Tooltip>
                )}
              </span>
            }
            tooltip={t("formSetting.domainNote")}
            rules={[
              {
                pattern: /^(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
                message: t("formSetting.domainError"),
              },
            ]}
          >
            <div className="flex gap-2 flex-col">
              {user?.setting?.domain_info?.status &&
                user?.setting?.domain_info?.status !== "active" && (
                  <div className="px-1 text-xs text-error">
                    {t("formSetting.domainWarning")}
                  </div>
                )}
              <div className="flex gap-2 items-center">
                <Input
                  className="py-2 px-3"
                  value={domain}
                  disabled={!ability.can("can", PERMISSION.PRIVATE_DOMAIN)}
                />
                <div
                  className={`py-1 px-3 flex gap-1 items-center cursor-pointer transition-all rounded-3xl ${
                    isLoadingReload
                      ? "text-secondary bg-[#0bcdac26] hover:bg-[#0bcdac26]"
                      : "hover:bg-gray-200/50"
                  } ${
                    !ability.can("can", PERMISSION.PRIVATE_DOMAIN) &&
                    "pointer-events-none opacity-50"
                  }`}
                  onClick={async () => {
                    setIsLoadingReload(true);
                    await fetchCheckDomain();
                    refetch();
                    setIsLoadingReload(false);
                  }}
                >
                  <RefreshCcw
                    className={`w-4 ${isLoadingReload ? "animate-spin" : ""}`}
                  />
                </div>
              </div>
            </div>
          </Form.Item>
          <div className="flex flex-col gap-3">
            {user?.setting?.domain_info?.name_servers && (
              <div className="text-sm flex items-center gap-1">
                <div>Name server</div>
                {user?.setting?.domain_info?.status === "active" && (
                  <Check className="text-secondary pb-1 w-7 h-7" />
                )}
              </div>
            )}
            {user?.setting?.domain_info?.name_servers?.map(
              (element: string, index: number) => {
                return (
                  <div key={index} className="flex gap-3 items-center">
                    <Input
                      className="flex-1 !cursor-default !text-gray-600 border-0 py-2 px-3 !bg-gray-100"
                      value={element}
                      disabled
                    />
                    <div
                      onClick={async () => {
                        const success = await copyToClipboard(element);
                        if (success) {
                          setCopy((prev) => [...prev, index]);
                          setTimeout(
                            () =>
                              setCopy((prev) =>
                                prev.filter((item) => item !== index)
                              ),
                            1000
                          );
                        }
                      }}
                      className="text-secondary flex gap-2 items-center cursor-pointer py-1 px-3 rounded-2xl hover:bg-[#0bcdac26] transition-all"
                    >
                      {copy.includes(index) ? (
                        <Check className="w-4" />
                      ) : (
                        <Clipboard className="w-4" />
                      )}
                      {t("dashboard.albums.action.download.modalShare.btnCopy")}
                    </div>
                  </div>
                );
              }
            )}
          </div>

          <Form.Item
            name="watermark"
            className="mt-3"
            label={t("formSetting.watermark")}
            tooltip={t("formSetting.watermarkNote")}
            rules={[
              {
                max: 20,
                message: t("formSetting.watermarkMaxLength", { value: 20 }),
              },
            ]}
          >
            <Input
              className="py-2 px-3"
              placeholder={t("formSetting.watermark")}
            />
          </Form.Item>

          <div className="relative flex mt-3 gap-3 mb-6">
            <div className="relative w-24 h-24">
              {typeof uploadedFile === "string" || !uploadedFile ? (
                <div
                  className={`relative w-full h-full ${
                    !ability.can("can", PERMISSION.CHANGE_LOGO) &&
                    "pointer-events-none opacity-50"
                  }`}
                >
                  <input
                    type="file"
                    ref={inputRef}
                    accept="image/gif, image/jpeg, image/png, image/jpg, image/webp"
                    className="!hidden"
                    onChange={handleChange}
                  />
                  {user?.setting?.main_logo ? (
                    <>
                      <div
                        onClick={handleOpenFile}
                        className="absolute top-1 right-1 flex justify-center items-center w-7 h-7 rounded-lg bg-gray-200/80 text-slate-700 cursor-pointer"
                      >
                        <Edit2 className="w-4" />
                      </div>
                      <img
                        src={user?.setting?.main_logo}
                        alt="thumbnail_url-default"
                        className="w-full h-full rounded-lg object-cover"
                      />
                    </>
                  ) : (
                    <div
                      onClick={handleOpenFile}
                      className={`relative w-full h-full flex justify-center items-center border border-dashed text-gray-600 border-gray-400 hover:border-secondary hover:bg-gray-50 rounded-lg cursor-pointer`}
                    >
                      <UploadIcon className="w-5" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative w-full h-full">
                  <div
                    onClick={() => {
                      setUpLoadedFile(undefined);
                    }}
                    className="absolute top-1 right-1 flex justify-center items-center cursor-pointer w-7 h-7 rounded-lg bg-gray-200/80 text-slate-700"
                  >
                    <Trash2 className="w-4" />
                  </div>
                  <img
                    src={URL.createObjectURL(uploadedFile)}
                    alt="uploaded"
                    className="w-full h-full rounded-lg object-cover"
                  />
                </div>
              )}
            </div>
            <div className="flex-1 w-full relative flex flex-col justify-between py-4">
              <div className="flex items-center gap-1">
                {t("formProfile.logo")}
                {!ability.can("can", PERMISSION.CHANGE_LOGO) && (
                  <Tooltip title={t("formSetting.subTitle")}>
                    <CrownOutlined className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  </Tooltip>
                )}
              </div>
              <span className="text-xs text-slate-500">
                {t("formSetting.noteLogo")}
              </span>
            </div>
            <span className="absolute -bottom-6 left-0 text-xs mb-1 block text-primary">
              {validUploaded}
            </span>
          </div>

          <div className="relative flex mt-3 gap-3 mb-6">
            <div className="relative w-24 h-24">
              {typeof uploadedFileDark === "string" || !uploadedFileDark ? (
                <div
                  className={`relative w-full h-full ${
                    !ability.can("can", PERMISSION.CHANGE_LOGO) &&
                    "pointer-events-none opacity-50"
                  }`}
                >
                  <input
                    type="file"
                    ref={inputRefDark}
                    accept="image/gif, image/jpeg, image/png, image/jpg, image/webp"
                    className="!hidden"
                    onChange={handleChangeDark}
                  />
                  {user?.setting?.main_logo_dark ? (
                    <>
                      <div
                        onClick={handleOpenFileDark}
                        className="absolute top-1 right-1 flex justify-center items-center w-7 h-7 rounded-lg bg-gray-200/80 text-slate-700 cursor-pointer"
                      >
                        <Edit2 className="w-4 dark:text-white" />
                      </div>
                      <img
                        src={user?.setting?.main_logo_dark}
                        alt="thumbnail_url-default"
                        className="w-full h-full rounded-lg object-cover"
                      />
                    </>
                  ) : (
                    <div
                      onClick={handleOpenFileDark}
                      className={`relative w-full h-full flex justify-center items-center border border-dashed text-gray-600 border-gray-400 hover:border-secondary hover:bg-gray-50 rounded-lg cursor-pointer`}
                    >
                      <UploadIcon className="w-5" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative w-full h-full">
                  <div
                    onClick={() => {
                      setUpLoadedFileDark(undefined);
                    }}
                    className="absolute top-1 right-1 flex justify-center items-center cursor-pointer w-7 h-7 rounded-lg bg-gray-200/80 text-slate-700"
                  >
                    <Trash2 className="w-4" />
                  </div>
                  <img
                    src={URL.createObjectURL(uploadedFileDark)}
                    alt="uploaded"
                    className="w-full h-full rounded-lg object-cover"
                  />
                </div>
              )}
            </div>
            <div className="flex-1 w-full relative flex flex-col justify-between py-4">
              <div className="flex items-center gap-1">
                {t("formProfile.logo")} (Dark)
                {!ability.can("can", PERMISSION.CHANGE_LOGO) && (
                  <Tooltip title={t("formSetting.subTitle")}>
                    <CrownOutlined className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  </Tooltip>
                )}
              </div>
              <span className="text-xs text-slate-500">
                {t("formSetting.noteLogoDark")}
              </span>
            </div>
            <span className="absolute -bottom-6 left-0 text-xs mb-1 block text-primary">
              {validUploadedDark}
            </span>
          </div>

          <div className="flex gap-3 w-full mb-6">
            <Upload
              accept=".jpg, .jpeg, .png, .gif, .webp, .favicon"
              showUploadList={false}
              beforeUpload={(file) => {
                setFav(file);
                return false;
              }}
              className={`!w-24 !h-24 relative cursor-pointer ${
                !ability.can("can", PERMISSION.CHANGE_LOGO) &&
                "pointer-events-none opacity-50"
              }`}
            >
              {typeof fav === "string" || !fav ? (
                <div className="relative !w-24 !h-24">
                  {user?.setting?.fav_logo ? (
                    <>
                      <div className="absolute top-1 right-1 flex justify-center items-center w-7 h-7 rounded-lg bg-gray-200/80 text-slate-700 cursor-pointer">
                        <Edit2 className="w-4 dark:text-white" />
                      </div>
                      <img
                        src={user?.setting?.fav_logo}
                        alt="thumbnail_url-default"
                        className="w-full h-full rounded-lg object-cover"
                      />
                    </>
                  ) : (
                    <div className="relative w-full h-full flex justify-center items-center border border-dashed text-gray-600 border-gray-400 hover:border-secondary hover:bg-gray-50 rounded-lg cursor-pointer">
                      <UploadIcon className="w-5" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative !w-24 !h-24">
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setFav(undefined);
                    }}
                    className="absolute top-1 right-1 flex justify-center items-center cursor-pointer w-7 h-7 rounded-lg bg-gray-200/80 text-slate-700"
                  >
                    <Trash2 className="w-4" />
                  </div>
                  <img
                    src={
                      typeof fav === "string" ? fav : URL.createObjectURL(fav)
                    }
                    alt="uploaded"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              )}
            </Upload>
            <div className="flex-1 w-full relative flex flex-col justify-between py-4">
              <div className="flex items-center gap-1">
                Favicon
                {!ability.can("can", PERMISSION.CHANGE_LOGO) && (
                  <Tooltip title={t("formSetting.subTitle")}>
                    <CrownOutlined className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  </Tooltip>
                )}
              </div>
              <span className="text-xs text-slate-500">
                {t("formSetting.noteFavicon")}
              </span>
            </div>
          </div>

          <div className="flex gap-3 w-full mb-6">
            <div className="relative w-24 h-24">
              {typeof metaImage === "string" || !metaImage ? (
                <div
                  className={`relative w-full h-full ${
                    !ability.can("can", PERMISSION.CHANGE_LOGO) &&
                    "pointer-events-none opacity-50"
                  }`}
                >
                  <input
                    type="file"
                    ref={inputRefMeta}
                    accept="image/gif, image/jpeg, image/png, image/jpg, image/webp"
                    className="!hidden"
                    onChange={handleChangeMeta}
                  />
                  {user?.setting?.meta_image ? (
                    <>
                      <div
                        onClick={handleOpenFileMeta}
                        className="absolute top-1 right-1 flex justify-center items-center w-7 h-7 rounded-lg bg-gray-200/80 text-slate-700 cursor-pointer"
                      >
                        <Edit2 className="w-4" />
                      </div>
                      <img
                        src={user?.setting?.meta_image}
                        alt="meta-image"
                        className="w-full h-full rounded-lg object-cover"
                      />
                    </>
                  ) : (
                    <div
                      onClick={handleOpenFileMeta}
                      className={`relative w-full h-full flex justify-center items-center border border-dashed text-gray-600 border-gray-400 hover:border-secondary hover:bg-gray-50 rounded-lg cursor-pointer`}
                    >
                      <UploadIcon className="w-5" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative w-full h-full">
                  <div
                    onClick={() => {
                      setMetaImage(undefined);
                    }}
                    className="absolute top-1 right-1 flex justify-center items-center cursor-pointer w-7 h-7 rounded-lg bg-gray-200/80 text-slate-700"
                  >
                    <Trash2 className="w-4" />
                  </div>
                  <img
                    src={URL.createObjectURL(metaImage)}
                    alt="uploaded"
                    className="w-full h-full rounded-lg object-cover"
                  />
                </div>
              )}
            </div>
            <div className="flex-1 w-full relative flex flex-col justify-between py-4">
              <div className="flex items-center gap-1">
                {t("formSetting.metaImage")}
                {!ability.can("can", PERMISSION.CHANGE_LOGO) && (
                  <Tooltip title={t("formSetting.subTitle")}>
                    <CrownOutlined className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  </Tooltip>
                )}
              </div>
              <span className="text-xs text-slate-500">
                {t("formSetting.noteMetaImage")}
              </span>
            </div>
            <span className="absolute -bottom-6 left-0 text-xs mb-1 block text-primary">
              {validMetaImage}
            </span>
          </div>

          <Form.Item>
            <Button
              type="primary"
              block
              htmlType="submit"
              className="transition-all mt-4 font-medium"
              size="large"
              loading={mutation.isLoading}
              disabled={mutation.isLoading}
              onMouseDown={() => {
                handleClick();
                handleClickDark();
                handleClickMeta();
              }}
            >
              {t("formProfile.btn")}
            </Button>
          </Form.Item>
        </Form>
      </div>
      <MessageModal
        isVisible={!!error || !!notification}
        onClose={() => {
          setError(null);
          setNotification(null);
        }}
        title={!!error ? t("Error") : notification}
        subTitle={error}
        type={!!error ? "error" : "success"}
        btnText={t("btnClose")}
      />
    </>
  );
}
